import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const History = () => {
  const server_folder = localStorage.getItem("server_folder");
  const server_url = localStorage.getItem("server_url");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const [isDefaultView, setIsDefaultView] = useState(true);
  
  // Add search state
  const [searchText, setSearchText] = useState("");
  const [filteredVehicles, setFilteredVehicles] = useState([]);

  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
  });

  useEffect(() => {
    // Load default last 50 records on component mount
    fetchVehicleHistory(true);
  }, []);

  // Add useEffect for local search
  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredVehicles(vehicles);
    } else {
      const searchLower = searchText.toLowerCase().trim();
      
      // Check if search text is for payment status
      const isPaidSearch = searchLower === "paid" || searchLower === "pay";
      const isUnpaidSearch = searchLower === "unpaid" || searchLower === "unpay" || searchLower === "due";
      const isActiveSearch = searchLower === "active" || searchLower === "live" || searchLower === "open";
      
      const filtered = vehicles.filter((vehicle) => {
        // Check for payment status first
        if (isPaidSearch && vehicle.payment_status == 1) return true;
        if (isUnpaidSearch && vehicle.payment_status != 1) return true;
        
        // Check for active/live vehicles
        if (isActiveSearch) {
          const isActive = !vehicle.vehicle_entry_out_date && !vehicle.vehicle_entry_out_time;
          return isActive;
        }
        
        // Regular search across all fields
        return (
          (vehicle.customer_name || "")
            .toLowerCase()
            .includes(searchLower) ||
          (vehicle.vehicle_reg_no || "")
            .toLowerCase()
            .includes(searchLower) ||
          (vehicle.customer_mobile || "")
            .toLowerCase()
            .includes(searchLower) ||
          (vehicle.brand_name || "")
            .toLowerCase()
            .includes(searchLower) ||
          (vehicle.model_name || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(vehicle.vehicle_entry_total_amt || "").includes(searchText) ||
          (vehicle.vehicle_entry_remark || "")
            .toLowerCase()
            .includes(searchLower) ||
          // Also check payment status text
          (vehicle.payment_status == 1 && "paid".includes(searchLower)) ||
          (vehicle.payment_status != 1 && "unpaid".includes(searchLower)) ||
          // Check active status
          (!vehicle.vehicle_entry_out_date && 
           !vehicle.vehicle_entry_out_time && 
           "active".includes(searchLower))
        );
      });
      setFilteredVehicles(filtered);
    }
  }, [searchText, vehicles]);

  const fetchVehicleHistory = async (isDefault = false) => {
    try {
      setLoading(true);
      const form = new FormData();
      form.append("company_id", "2");
      form.append("customer_id", "");
      form.append("vehicle_entry_grage_id", localStorage.getItem("grage_id"));

      // Only add date filters if they're provided (not default view)
      if (!isDefault && filters.from_date && filters.to_date) {
        form.append("from_date", filters.from_date);
        form.append("to_date", filters.to_date);
      } else {
        // Clear date filters for default view
        form.append("from_date", "");
        form.append("to_date", "");
      }

      const response = await axios.post(
        `${server_url}vehicle_history.php`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const res = response.data;

      if (res.status == "1") {
        setVehicles(res.result || []);
        setFilteredVehicles(res.result || []); // Initialize filteredVehicles
        setRecordCount(res.record_count);

        // If this is the default view (no date filter), set the date range from the response
        if (isDefault && res.date_range) {
          // Convert date format from "d-M-Y" to "YYYY-MM-DD" for date inputs
          const fromDate = res.date_range.from_date
            ? new Date(res.date_range.from_date.split("-").reverse().join("-"))
                .toISOString()
                .split("T")[0]
            : "";
          const toDate = res.date_range.to_date
            ? new Date(res.date_range.to_date.split("-").reverse().join("-"))
                .toISOString()
                .split("T")[0]
            : "";

          setFilters({
            from_date: fromDate,
            to_date: toDate,
          });
          setIsDefaultView(true);
        } else if (!isDefault) {
          setIsDefaultView(false);
        }
      } else {
        setVehicles([]);
        setFilteredVehicles([]);
        setRecordCount(0);
      }
    } catch (err) {
      console.error("fetchVehicleHistory error:", err);
      setVehicles([]);
      setFilteredVehicles([]);
      setRecordCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (filters.from_date && filters.to_date) {
      fetchVehicleHistory(false);
    }
  };

  const handleReset = () => {
    setFilters({
      from_date: "",
      to_date: "",
    });
    setSearchText(""); // Clear search text on reset
    fetchVehicleHistory(true);
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };

  const isVehicleActive = (vehicle) => {
    return !vehicle.vehicle_entry_out_date && !vehicle.vehicle_entry_out_time;
  };

  // Helper function to get payment status badge
  const getPaymentStatusBadge = (vehicle) => {
    if (vehicle.payment_status == 1) {
      return (
        <span style={{ 
          color: "#27ae60", 
          fontSize: "12px",
          fontWeight: "500"
        }}>
          Paid
        </span>
      );
    } else {
      return (
        <span style={{ 
          color: "#e74c3c", 
          fontSize: "12px",
          fontWeight: "500"
        }}>
          Unpaid
        </span>
      );
    }
  };

  // Helper function to get active status badge
  const getActiveStatusBadge = (vehicle) => {
    if (isVehicleActive(vehicle)) {
      return (
        <span style={{ 
          color: "#0352a1", 
          fontSize: "12px",
          fontWeight: "500",
          marginLeft: "8px"
        }}>
          • Active
        </span>
      );
    }
    return null;
  };

  return (
    <div className="history-container">
      {/* Sticky Search Filter Section */}
      <div
        className="sticky-filter"
        style={{ position: "fixed", top: "56px", width: "100%" }}
      >
        <div className="home-icon">
          <div className="section-home">
            <form onSubmit={handleSearch} className="search-form">
              <div className="input-group same-line">
                <input
                  type="date"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                  className="search-input"
                  placeholder="From Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                  className="search-input"
                  placeholder="To Date"
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>
            {/* Search Bar Below Date Filters */}
            <div style={{ marginTop: "10px" }}>
              <div className="input-group same-line">
                <input
                  type="text"
                  placeholder="Search by customer, vehicle, mobile, paid/unpaid, active..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyPress={(e) => e.key == "Enter" && handleSearch()}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="content-area">
        {/* Vehicle List */}
        <div className="home-vehicles">
          {loading ? (
            <div className="loading-vehicles">
              <div className="loading-spinner"></div>
              <div>Loading records...</div>
            </div>
          ) : filteredVehicles.length > 0 ? (
            <div className="vehicles-list home-icon">
              {filteredVehicles.map((vehicle, index) => (
                <div key={index} className="vehicle-card section-home">
                  <div className="vehicle-header">
                    <Link
                      to={`/job-card-details/${vehicle.vehicle_entry_id}`}
                      state={{ vehicle: vehicle }}
                      style={{ width: "100%" }}
                    >
                      {isVehicleActive(vehicle) && (
                        <img
                          src={server_folder + "/img/live.gif"}
                          style={{ float: "right", height: "20px" }}
                          alt="Active"
                        />
                      )}
                      <table style={{ width: "100%", textAlign: "left" }}>
                        <tbody>
                          <tr>
                            <td
                              style={{
                                width: "60%",
                                fontWeight: "bold",
                                fontSize: "16px",
                              }}
                            >
                              {vehicle.customer_name || "N/A"}
                              {getActiveStatusBadge(vehicle)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                color: "#0352a1",
                              }}
                            >
                              {vehicle.customer_mobile || "N/A"}
                            </td>
                          </tr>

                          <tr>
                            <td style={{ width: "60%", color: "#2c3e50" }}>
                              {vehicle.vehicle_reg_no || "N/A"}
                            </td>
                            <td style={{ textAlign: "right", color: "#666" }}>
                              {vehicle.brand_name || ""}{" "}
                              {vehicle.model_name || ""}
                            </td>
                          </tr>

                          {/* Service Details */}
                          <tr>
                            <td
                              style={{
                                width: "60%",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              Estimate: #{vehicle.vehicle_entry_no}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                color: "#27ae60",
                              }}
                            >
                              {formatAmount(vehicle.vehicle_entry_total_amt)}
                            </td>
                          </tr>

                          {/* Entry Date */}
                          <tr>
                            <td
                              style={{
                                width: "60%",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              Entry: {vehicle.vehicle_entry_in_date}{" "}
                              {vehicle.vehicle_entry_in_time}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                fontSize: "14px",
                                color:
                                  vehicle.payment_status == 1
                                    ? "#27ae60"
                                    : "#e74c3c",
                              }}
                            >
                              {vehicle.payment_status == 1 ? (
                                <span>Paid</span>
                              ) : (
                                <>
                                  <a
                                    href={`https://wa.me/91${vehicle.customer_mobile.replace(
                                      /[^0-9]/g,
                                      ""
                                    )}?text=Hello ${
                                      vehicle.customer_name
                                    },%0A%0AThis is a gentle reminder regarding the pending payment for your vehicle service.%0A%0A🚗 Vehicle No: ${
                                      vehicle.vehicle_reg_no
                                    }%0A📅 Service Date: ${new Date(
                                      vehicle.vehicle_entry_date
                                    ).toLocaleDateString()}%0A💰 Amount Due: ${
                                      formatAmount(
                                        vehicle.vehicle_entry_total_amt
                                      ) || "0"
                                    }%0A%0AKindly complete the payment at your convenience.%0AThank you 🙏`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#e74c3c",
                                      textDecoration: "none",
                                      fontSize: "11px",
                                      padding: "4px 8px",
                                      border: "1px solid #e74c3c",
                                      borderRadius: "4px",
                                      marginRight: "8px",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <i className="fab fa-whatsapp"></i>
                                  </a>
                                  Unpaid
                                </>
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td
                              colSpan={2}
                              style={{
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              Exit: {vehicle.vehicle_entry_out_date || "Not yet"}{" "}
                              {vehicle.vehicle_entry_out_time || ""}
                            </td>
                          </tr>

                          {vehicle.vehicle_entry_remark && (
                            <tr>
                              <td
                                colSpan="2"
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  fontStyle: "italic",
                                  paddingTop: "8px",
                                }}
                              >
                                📝 {vehicle.vehicle_entry_remark}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-vehicles section-home">
              <div className="no-vehicles-icon">📋</div>
              <div className="no-vehicles-title">No Records Found</div>
              <div className="no-vehicles-text">
                {searchText ? (
                  <>
                    No records found for "{searchText}"<br />
                    Try: <strong>paid</strong>, <strong>unpaid</strong>, <strong>active</strong>, or search by name/vehicle
                  </>
                ) : filters.from_date || filters.to_date ? (
                  "Try different date range or reset to view last 50 records"
                ) : (
                  "No service records found"
                )}
              </div>
              <button onClick={handleReset} className="reset-default-btn">
                <i className="fas fa-history"></i> Show Last 50 Records
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .history-container {
          min-height: 100vh;
          background: white;
          padding: 0;
        }

        .sticky-filter {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .content-area {
          padding-top: 110px; /* Increased for quick filters */
          padding-bottom: 20px;
        }

        .home-icon .section-home {
          padding: 8px 12px 12px 12px;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }

        .input-group.same-line {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .search-input {
          flex: 1;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          background: #f8f9fa;
        }

        .date-separator {
          color: #718096;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        .search-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quick-filter-btn {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .quick-filter-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .record-count {
          font-size: 14px;
          font-weight: 500;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .default-indicator {
          margin-top: 8px;
        }

        .default-badge {
          background: #e3f2fd;
          color: #0352a1;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .default-badge i {
          font-size: 14px;
        }

        .home-vehicles {
          padding: 20px 10px;
        }

        .loading-vehicles {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .vehicles-list {
          display: grid;
          gap: 15px;
        }

        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #0352a1;
          transition: all 0.3s ease;
        }

        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 20px 0;
        }

        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }

        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .reset-default-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 14px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .reset-default-btn:hover {
          background: #023e7d;
        }

        @media (max-width: 768px) {
          .content-area {
            padding-top: 260px; /* Adjusted for mobile */
          }

          .home-vehicles {
            padding: 15px 8px;
          }

          .vehicle-card {
            padding: 12px;
          }

          .vehicle-header td {
            font-size: 14px;
          }

          .sticky-filter {
            padding: 6px 0;
          }

          .input-group.same-line {
            flex-wrap: wrap;
          }

          .search-input {
            min-width: 120px;
          }

          .date-separator {
            display: none;
          }

          .quick-filter-btn {
            padding: 5px 10px;
            font-size: 11px;
          }
        }

        @media (max-width: 480px) {
          .content-area {
            padding-top: 110px;
          }

          .sticky-filter {
            padding: 4px 0;
          }

          .input-group.same-line {
            gap: 6px;
          }

          .search-input {
            font-size: 13px;
            padding: 8px 10px;
          }

          .search-btn {
            padding: 8px 10px;
            min-width: 36px;
          }

          .quick-filter-btn {
            padding: 4px 8px;
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default History;